import { Directive, EventEmitter, HostListener, Output, ElementRef, Input } from '@angular/core';

@Directive({
	selector: '[appBarcodeDetect]'
})
export class BarcodeDetectDirective {
	@Output() barcodeDetected = new EventEmitter<{
		barcode: string;
		originalValue: any;
		fieldType: 'qty' | 'price';
	}>();

	@Input() fieldType: 'qty' | 'price' = 'qty';

	private lastKeyTime = 0;
	private buffer = '';
	private originalValue: any = '';
	private resetTimeout: any = null;
	private readonly SCAN_THRESHOLD_MS = 100;
	private readonly MIN_BARCODE_LENGTH = 6;
	private readonly RESET_THRESHOLD_MS = 200;
	private isDetecting = false;

	constructor(
		private elementRef: ElementRef
	) {}

	@HostListener('focus', ['$event'])
	onFocus(event: FocusEvent): void {
		// Store original value when field is focused
		const input = this.elementRef.nativeElement as HTMLInputElement;
		this.originalValue = input.value || '';
		this.buffer = '';
		this.lastKeyTime = 0;
		this.isDetecting = false;
	}

	@HostListener('input', ['$event'])
	onInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		
		// If field is disabled, don't detect
		if (input.disabled) {
			return;
		}
	}

	@HostListener('keydown', ['$event'])
	onKeyDown(event: KeyboardEvent): void {
		const input = this.elementRef.nativeElement as HTMLInputElement;
		
		// If field is disabled, don't detect
		if (input.disabled) {
			return;
		}

		const currentTime = Date.now();
		const key = event.key;

		// Handle backspace/delete - cancel detection
		if (key === 'Backspace' || key === 'Delete') {
			this.resetDetection();
			return;
		}

		// Handle Enter key - check if we have a valid barcode
		if (key === 'Enter') {
			event.preventDefault();
			
			if (this.buffer.length >= this.MIN_BARCODE_LENGTH && this.isDetecting) {
				// Valid barcode detected
				const barcode = this.buffer.trim();
				this.emitBarcode(barcode);
				this.resetDetection();
			} else {
				// Not a barcode, allow normal Enter behavior
				this.resetDetection();
			}
			return;
		}

		// Handle single character input
		if (key.length === 1) {
			const timeSinceLastKey = currentTime - this.lastKeyTime;

			// If too much time has passed, reset buffer and capture new original value
			if (timeSinceLastKey > this.RESET_THRESHOLD_MS) {
				// Capture current value as original before starting new detection
				const input = this.elementRef.nativeElement as HTMLInputElement;
				this.originalValue = input.value || '';
				this.resetDetection();
			}

			// Add character to buffer if within threshold
			if (timeSinceLastKey <= this.SCAN_THRESHOLD_MS || this.lastKeyTime === 0) {
				// If this is the first character in rapid sequence, capture original value
				// keydown fires before the value changes, so current value is the original
				if (this.lastKeyTime === 0 || !this.isDetecting) {
					const input = this.elementRef.nativeElement as HTMLInputElement;
					this.originalValue = input.value || '';
				}

				this.buffer += key;
				this.isDetecting = true;
				this.lastKeyTime = currentTime;

				// Clear any existing reset timeout
				if (this.resetTimeout) {
					clearTimeout(this.resetTimeout);
				}

				// Set timeout to reset if no more characters come
				this.resetTimeout = setTimeout(() => {
					if (this.buffer.length < this.MIN_BARCODE_LENGTH) {
						this.resetDetection();
					}
				}, this.RESET_THRESHOLD_MS);
			} else {
				// Too slow, reset and capture new original value
				const input = this.elementRef.nativeElement as HTMLInputElement;
				this.originalValue = input.value || '';
				this.resetDetection();
			}
		}
	}

	private emitBarcode(barcode: string): void {
		const input = this.elementRef.nativeElement as HTMLInputElement;
		
		// Restore original value immediately
		setTimeout(() => {
			input.value = this.originalValue;
			// Trigger input event to update Angular binding
			const event = new Event('input', { bubbles: true });
			input.dispatchEvent(event);
		}, 0);

		// Emit barcode event
		this.barcodeDetected.emit({
			barcode: barcode,
			originalValue: this.originalValue,
			fieldType: this.fieldType
		});
	}

	private resetDetection(): void {
		this.buffer = '';
		this.lastKeyTime = 0;
		this.isDetecting = false;
		if (this.resetTimeout) {
			clearTimeout(this.resetTimeout);
			this.resetTimeout = null;
		}
	}
}

